import * as Dialog from "@radix-ui/react-dialog";
import type { ShippingProfile, ShippingRule } from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Pencil, Plus, Save, Trash2, Truck, Workflow, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { ShippingRuleEditor } from "@/components/ShippingRuleEditor";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type PackagingInput, type PackagingItem } from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const TABS = [
  { key: "methods", label: "Versandarten", icon: Truck },
  { key: "packagings", label: "Verpackungen", icon: Box },
  { key: "rules", label: "Globale Regeln", icon: Workflow },
] as const;

const errMsg = (e: unknown, f: string) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);
const euro = (minor: number) => (minor / 100).toFixed(2).replace(".", ",");
const toMinor = (s: string) => Math.round(Number(s.replace(",", ".")) * 100) || 0;
const num = (s: string) => (s.trim() === "" ? undefined : Number(s.replace(",", ".")));

/** Logiful-intern: Grenzen und Einkaufspreise je Versandart, Verpackungskatalog, globale Regeln */
export function AdminShippingPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as (typeof TABS)[number]["key"]) ?? "methods";
  return (
    <div className="pb-16">
      <PageHeader title="Versand" description="Grundlage für die automatische Versandart-Wahl: was jede Versandart kann und kostet, welche Verpackungen es gibt und welche Regeln für alle Kunden gelten. Für Kunden unsichtbar." />
      <div className="px-4 md:px-8 space-y-4 max-w-6xl">
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setParams({ tab: t.key }, { replace: true })} className={cx("h-8 px-3 rounded-md text-[13px] font-medium inline-flex items-center gap-1.5", tab === t.key ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
              <t.icon className="size-3.5" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "methods" && <MethodsTab />}
        {tab === "packagings" && <PackagingsTab />}
        {tab === "rules" && <GlobalRulesTab />}
      </div>
    </div>
  );
}

// ── Versandarten ─────────────────────────────────────────────────────

function MethodsTab() {
  const q = useQuery({ queryKey: ["shipping-profiles"], queryFn: () => api.shipping.profiles() });
  const [edit, setEdit] = useState<{ methodId: string; name: string; carrier: string | null; def?: ShippingProfile } | null>(null);
  if (q.isLoading) return <Skeleton className="h-60 w-full" />;
  const profiles = new Map((q.data?.profiles ?? []).map((p) => [p.methodId, p.definition]));
  const rows = [...(q.data?.available ?? [])];
  for (const p of q.data?.profiles ?? []) if (!rows.some((r) => r.methodId === p.methodId)) rows.push({ methodId: p.methodId, name: p.definition.name, carrier: p.definition.carrier, type: null, warehouses: [] });

  return (
    <>
      <Card className="overflow-hidden">
        <table className="w-full text-left text-[13px]">
          <thead className="text-[12px] text-fg-subtle bg-surface-2/50 border-b border-border">
            <tr><th className="h-9 pl-4 pr-3 font-medium">Versandart</th><th className="h-9 px-3 font-medium">Lager</th><th className="h-9 px-3 font-medium">Grenzen</th><th className="h-9 px-3 font-medium">Einkaufspreis ab</th><th className="h-9 pl-3 pr-4" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = profiles.get(r.methodId);
              const from = p?.prices.length ? Math.min(...p.prices.map((x) => x.priceMinor)) : null;
              return (
                <tr key={r.methodId} className="border-b border-border last:border-0">
                  <td className="pl-4 pr-3 py-2.5"><div className="font-medium">{r.name}</div><div className="text-[12px] text-fg-muted">{r.carrier ?? "—"} · <span className="font-mono">{r.methodId}</span></div></td>
                  <td className="px-3 py-2.5 text-[12px] text-fg-muted">{r.warehouses.join(", ") || "—"}</td>
                  <td className="px-3 py-2.5 text-[12px]">
                    {p ? (
                      <span className="text-fg-muted">{[p.maxWeightKg && `bis ${p.maxWeightKg} kg`, p.maxDims && `${p.maxDims.l}×${p.maxDims.w}×${p.maxDims.h} cm`, p.countries.length ? p.countries.join(", ") : null, p.dangerousGoods && "Gefahrgut"].filter(Boolean).join(" · ") || "keine Grenzen"}</span>
                    ) : <Badge tone="warning">nicht gepflegt</Badge>}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums">{from != null ? fmtMoney(from, "EUR") : <span className="text-fg-subtle">—</span>}</td>
                  <td className="pl-3 pr-4 py-2.5 text-right"><Button size="sm" icon={<Pencil className="size-3.5" />} onClick={() => setEdit({ methodId: r.methodId, name: r.name, carrier: r.carrier, ...(p ? { def: p } : {}) })}>{p ? "Bearbeiten" : "Einrichten"}</Button></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-fg-muted">Noch keine Versandarten – sie erscheinen, sobald ein Kunde sein Lager verbunden hat.</td></tr>}
          </tbody>
        </table>
      </Card>
      <p className="text-[12px] text-fg-subtle">Erst gepflegte Versandarten nimmt die automatische Wahl in Betracht. Ohne Einkaufspreise kann nicht nach Kosten optimiert werden.</p>
      {edit && <ProfileDialog {...edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return <label className={cx("block", className)}><span className="text-[12px] font-medium text-fg-muted">{label}</span><div className="mt-1">{children}</div></label>;
}

export function ProfileDialog({ methodId, name, carrier, def, onClose, tenantId }: { methodId: string; name: string; carrier: string | null; def?: ShippingProfile; onClose: () => void; tenantId?: string }) {
  const qc = useQueryClient();
  const [f, setF] = useState(() => ({
    name: def?.name ?? name,
    customerLabel: def?.customerLabel ?? "",
    carrier: def?.carrier ?? carrier ?? "",
    service: def?.service ?? "standard",
    active: def?.active ?? true,
    maxWeightKg: def?.maxWeightKg?.toString() ?? "",
    l: def?.maxDims?.l.toString() ?? "",
    w: def?.maxDims?.w.toString() ?? "",
    h: def?.maxDims?.h.toString() ?? "",
    maxGirthCm: def?.maxGirthCm?.toString() ?? "",
    countries: (def?.countries ?? []).join(", "),
    excludeCountries: (def?.excludeCountries ?? []).join(", "),
    dangerousGoods: def?.dangerousGoods ?? false,
    contract: def?.contract ?? (tenantId ? "customer" : "logiful"),
    prices: (def?.prices ?? [{ countries: ["DE"], upToKg: 31.5, priceMinor: 0 }]).map((p) => ({ zone: p.countries === "*" ? "*" : p.countries.join(", "), upToKg: p.upToKg.toString(), price: euro(p.priceMinor) })),
    surcharges: (def?.surcharges ?? []).map((s) => ({ label: s.label, overLengthCm: s.overLengthCm?.toString() ?? "", overGirthCm: s.overGirthCm?.toString() ?? "", overKg: s.overKg?.toString() ?? "", price: euro(s.priceMinor) })),
  }));
  const list = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
  const save = useMutation({
    mutationFn: () => {
      const d = num(f.l) && num(f.w) && num(f.h) ? { maxDims: { l: num(f.l)!, w: num(f.w)!, h: num(f.h)! } } : {};
      return api.shipping.saveProfile(methodId, {
        name: f.name, customerLabel: f.customerLabel.trim(), carrier: f.carrier || null, service: f.service, active: f.active,
        ...(num(f.maxWeightKg) ? { maxWeightKg: num(f.maxWeightKg)! } : {}), ...d, ...(num(f.maxGirthCm) ? { maxGirthCm: num(f.maxGirthCm)! } : {}),
        countries: list(f.countries), excludeCountries: list(f.excludeCountries), dangerousGoods: f.dangerousGoods, contract: f.contract,
        prices: f.prices.filter((p) => num(p.upToKg)).map((p) => ({ countries: p.zone.trim() === "*" || !p.zone.trim() ? ("*" as const) : list(p.zone), upToKg: num(p.upToKg)!, priceMinor: toMinor(p.price) })),
        surcharges: f.surcharges.filter((s) => s.label.trim()).map((s) => ({ label: s.label.trim(), ...(num(s.overLengthCm) ? { overLengthCm: num(s.overLengthCm)! } : {}), ...(num(s.overGirthCm) ? { overGirthCm: num(s.overGirthCm)! } : {}), ...(num(s.overKg) ? { overKg: num(s.overKg)! } : {}), priceMinor: toMinor(s.price) })),
      }, tenantId);
    },
    onSuccess: () => { toast.success(`${f.name} gespeichert`); void qc.invalidateQueries({ queryKey: ["shipping-profiles"] }); onClose(); },
    onError: (e) => toast.error(errMsg(e, "Speichern fehlgeschlagen – bitte Eingaben prüfen")),
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 right-0 top-0 h-full w-[min(640px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{f.name}</Dialog.Title>
              <Dialog.Description className="text-[12.5px] text-fg-muted"><span className="font-mono">{methodId}</span>{tenantId ? " · Kundenprofil (gilt nur für diesen Kunden)" : " · allgemeines Profil (alle Kunden)"}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 grid place-items-center rounded-lg hover:bg-surface-2 text-fg-muted" aria-label="Schließen"><X className="size-4" /></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 text-[13px]">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
              <Field label="Bezeichnung für Kunden"><Input value={f.customerLabel} onChange={(e) => set("customerLabel", e.target.value)} placeholder="z. B. DHL Paket, DHL Kleinpaket" /></Field>
              <Field label="Dienstleister"><Input value={f.carrier} onChange={(e) => set("carrier", e.target.value)} placeholder="z. B. DHL" /></Field>
              <Field label="Service-Stufe">
                <select value={f.service} onChange={(e) => set("service", e.target.value as typeof f.service)} className="h-8 w-full rounded-lg border border-border bg-surface px-2">
                  <option value="economy">Economy (z. B. Warenpost)</option><option value="standard">Standard</option><option value="express">Express</option>
                </select>
              </Field>
              <div className="flex items-end gap-4 pb-1">
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} /> aktiv</label>
                <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.dangerousGoods} onChange={(e) => set("dangerousGoods", e.target.checked)} /> Gefahrgut erlaubt</label>
                {tenantId && (
                  <label className="inline-flex items-center gap-2" title="Wählt eine Regel eine Versandart über den Namen (z. B. „Kleinpaket“), wird ein passender eigener Vertrag immer bevorzugt">
                    <input type="checkbox" checked={f.contract === "customer"} onChange={(e) => set("contract", e.target.checked ? "customer" : "logiful")} /> Eigener Versandvertrag des Kunden
                  </label>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-fg-subtle">Grenzen je Paket</h3>
              <div className="mt-2 grid grid-cols-5 gap-2">
                <Field label="Max. Gewicht (kg)"><Input value={f.maxWeightKg} onChange={(e) => set("maxWeightKg", e.target.value)} inputMode="decimal" /></Field>
                <Field label="Länge (cm)"><Input value={f.l} onChange={(e) => set("l", e.target.value)} inputMode="decimal" /></Field>
                <Field label="Breite (cm)"><Input value={f.w} onChange={(e) => set("w", e.target.value)} inputMode="decimal" /></Field>
                <Field label="Höhe (cm)"><Input value={f.h} onChange={(e) => set("h", e.target.value)} inputMode="decimal" /></Field>
                <Field label="Gurtmaß (cm)"><Input value={f.maxGirthCm} onChange={(e) => set("maxGirthCm", e.target.value)} inputMode="decimal" /></Field>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Field label="Nur in diese Länder (leer = alle)"><Input value={f.countries} onChange={(e) => set("countries", e.target.value)} placeholder="DE" className="font-mono" /></Field>
                <Field label="Nicht in diese Länder"><Input value={f.excludeCountries} onChange={(e) => set("excludeCountries", e.target.value)} placeholder="CH, NO" className="font-mono" /></Field>
              </div>
            </div>
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-fg-subtle">Einkaufspreise je Paket</h3>
              <p className="text-[12px] text-fg-subtle">Zone: Länderliste oder „*“ für alle übrigen. Die günstigste Staffel, in die das Paketgewicht passt, zählt.</p>
              <div className="mt-2 space-y-1.5">
                {f.prices.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={p.zone} onChange={(e) => set("prices", f.prices.map((x, j) => (j === i ? { ...x, zone: e.target.value } : x)))} placeholder="DE oder *" className="w-36 font-mono" />
                    <span className="text-fg-muted">bis</span><Input value={p.upToKg} onChange={(e) => set("prices", f.prices.map((x, j) => (j === i ? { ...x, upToKg: e.target.value } : x)))} className="w-20 tabular-nums" inputMode="decimal" /><span className="text-fg-muted">kg</span>
                    <Input value={p.price} onChange={(e) => set("prices", f.prices.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className="w-24 tabular-nums" inputMode="decimal" suffix="€" />
                    <button type="button" onClick={() => set("prices", f.prices.filter((_, j) => j !== i))} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label="Staffel entfernen"><Trash2 className="size-3.5" /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => set("prices", [...f.prices, { zone: "DE", upToKg: "", price: "" }])}>Staffel</Button>
              </div>
            </div>
            <div>
              <h3 className="text-[12px] font-semibold uppercase tracking-wide text-fg-subtle">Zuschläge je Paket</h3>
              <div className="mt-2 space-y-1.5">
                {f.surcharges.map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <Input value={s.label} onChange={(e) => set("surcharges", f.surcharges.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} placeholder="z. B. Sperrgut" className="w-36" />
                    <span className="text-fg-muted text-[12px]">ab Länge</span><Input value={s.overLengthCm} onChange={(e) => set("surcharges", f.surcharges.map((x, j) => (j === i ? { ...x, overLengthCm: e.target.value } : x)))} className="w-16" />
                    <span className="text-fg-muted text-[12px]">oder Gurtmaß</span><Input value={s.overGirthCm} onChange={(e) => set("surcharges", f.surcharges.map((x, j) => (j === i ? { ...x, overGirthCm: e.target.value } : x)))} className="w-16" />
                    <span className="text-fg-muted text-[12px]">oder kg</span><Input value={s.overKg} onChange={(e) => set("surcharges", f.surcharges.map((x, j) => (j === i ? { ...x, overKg: e.target.value } : x)))} className="w-16" />
                    <Input value={s.price} onChange={(e) => set("surcharges", f.surcharges.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} className="w-24" suffix="€" />
                    <button type="button" onClick={() => set("surcharges", f.surcharges.filter((_, j) => j !== i))} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label="Zuschlag entfernen"><Trash2 className="size-3.5" /></button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => set("surcharges", [...f.surcharges, { label: "", overLengthCm: "", overGirthCm: "", overKg: "", price: "" }])}>Zuschlag</Button>
              </div>
            </div>
          </div>
          <div className="px-6 py-3.5 border-t border-border flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">Abbrechen</Button></Dialog.Close>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Speichern</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Verpackungen ─────────────────────────────────────────────────────

const EMPTY_PACK: PackagingInput = { name: "", kind: "box", warehouseRef: null, innerLengthCm: 0, innerWidthCm: 0, innerHeightCm: 0, tareKg: 0.2, maxKg: 31.5, costMinor: 0, active: true };

function PackagingsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["packagings"], queryFn: api.shipping.packagings });
  const [edit, setEdit] = useState<(PackagingInput & { id?: string }) | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => api.shipping.deletePackaging(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["packagings"] }) });
  if (q.isLoading) return <Skeleton className="h-40 w-full" />;
  return (
    <>
      <div className="flex justify-end"><Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEdit({ ...EMPTY_PACK })}>Verpackung</Button></div>
      <Card className="overflow-hidden">
        <table className="w-full text-left text-[13px]">
          <thead className="text-[12px] text-fg-subtle bg-surface-2/50 border-b border-border">
            <tr><th className="h-9 pl-4 pr-3 font-medium">Verpackung</th><th className="h-9 px-3 font-medium">Innenmaße</th><th className="h-9 px-3 font-medium text-right">Leergewicht</th><th className="h-9 px-3 font-medium text-right">max.</th><th className="h-9 px-3 font-medium text-right">Kosten</th><th className="h-9 pl-3 pr-4" /></tr>
          </thead>
          <tbody>
            {(q.data ?? []).map((p: PackagingItem) => (
              <tr key={p.id} className={cx("border-b border-border last:border-0", !p.active && "opacity-50")}>
                <td className="pl-4 pr-3 py-2.5"><div className="font-medium">{p.name}</div><div className="text-[12px] text-fg-muted">{p.kind === "bag" ? "Versandtasche (flexibel)" : "Karton"}{p.warehouseRef ? ` · Lager ${p.warehouseRef}` : ""}</div></td>
                <td className="px-3 py-2.5 tabular-nums">{p.innerLengthCm} × {p.innerWidthCm} × {p.innerHeightCm} cm</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{p.tareKg} kg</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{p.maxKg} kg</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(p.costMinor, "EUR")}</td>
                <td className="pl-3 pr-4 py-2.5 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={() => setEdit(p)}>Bearbeiten</Button>
                  <button onClick={() => { if (confirm(`${p.name} löschen?`)) remove.mutate(p.id); }} className="ml-1 size-7 inline-grid place-items-center rounded-md text-fg-subtle hover:text-danger align-middle" aria-label="löschen"><Trash2 className="size-3.5" /></button>
                </td>
              </tr>
            ))}
            {q.data?.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-fg-muted">Noch keine Verpackungen. Ohne Verpackungen wird nur nach Artikelgewicht entschieden.</td></tr>}
          </tbody>
        </table>
      </Card>
      {edit && <PackagingDialog value={edit} onClose={() => setEdit(null)} />}
    </>
  );
}

function PackagingDialog({ value, onClose }: { value: PackagingInput & { id?: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ ...value, cost: euro(value.costMinor) });
  const save = useMutation({
    mutationFn: () => {
      const { id, cost, ...rest } = f;
      const body = { ...rest, costMinor: toMinor(cost) };
      return id ? api.shipping.updatePackaging(id, body) : api.shipping.createPackaging(body);
    },
    onSuccess: () => { toast.success("Verpackung gespeichert"); void qc.invalidateQueries({ queryKey: ["packagings"] }); onClose(); },
    onError: (e) => toast.error(errMsg(e, "Bitte Maße und Gewichte prüfen")),
  });
  const n = (k: "innerLengthCm" | "innerWidthCm" | "innerHeightCm" | "tareKg" | "maxKg") => (
    <Input value={String(f[k] || "")} onChange={(e) => setF({ ...f, [k]: Number(e.target.value.replace(",", ".")) || 0 })} inputMode="decimal" className="tabular-nums" />
  );
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(520px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[17px] font-semibold">{value.id ? "Verpackung bearbeiten" : "Neue Verpackung"}</Dialog.Title>
          <div className="mt-4 grid grid-cols-3 gap-3 text-[13px]">
            <Field label="Name" className="col-span-2"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Karton M" /></Field>
            <Field label="Art"><select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as "box" | "bag" })} className="h-8 w-full rounded-lg border border-border bg-surface px-2"><option value="box">Karton</option><option value="bag">Versandtasche</option></select></Field>
            <Field label="Innen Länge (cm)">{n("innerLengthCm")}</Field>
            <Field label="Breite (cm)">{n("innerWidthCm")}</Field>
            <Field label="Höhe (cm)">{n("innerHeightCm")}</Field>
            <Field label="Leergewicht (kg)">{n("tareKg")}</Field>
            <Field label="Max. Gewicht (kg)">{n("maxKg")}</Field>
            <Field label="Materialkosten"><Input value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} suffix="€" inputMode="decimal" /></Field>
            <label className="col-span-3 inline-flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> aktiv</label>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button variant="ghost">Abbrechen</Button></Dialog.Close>
            <Button variant="primary" disabled={!f.name.trim() || !f.innerLengthCm || !f.innerWidthCm || !f.innerHeightCm} loading={save.isPending} onClick={() => save.mutate()}>Speichern</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Globale Regeln ───────────────────────────────────────────────────

function GlobalRulesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shipping-global-rules"], queryFn: api.shipping.globalRules });
  const profiles = useQuery({ queryKey: ["shipping-profiles"], queryFn: () => api.shipping.profiles() });
  const packs = useQuery({ queryKey: ["packagings"], queryFn: api.shipping.packagings });
  const [rules, setRules] = useState<ShippingRule[] | null>(null);
  useEffect(() => { if (q.data && rules === null) setRules(q.data.rules); }, [q.data, rules]);
  const save = useMutation({
    mutationFn: () => api.shipping.saveGlobalRules(rules ?? []),
    onSuccess: (r) => { toast.success(`Globale Regeln gespeichert (Version ${r.version})`); void qc.invalidateQueries({ queryKey: ["shipping-global-rules"] }).then(() => setRules(null)); },
  });
  const methods = [...(profiles.data?.available ?? []).map((m) => ({ value: m.methodId, label: `${m.name}${m.carrier ? ` (${m.carrier})` : ""}` }))];
  if (q.isLoading || rules === null) return <Skeleton className="h-40 w-full" />;
  return (
    <>
      <div className="rounded-xl border border-border bg-surface-2/40 p-3 text-[12.5px] text-fg-muted">Gelten für <b className="text-fg">alle Kunden</b>, immer intern (für Kunden unsichtbar) und nach den Regeln des jeweiligen Kunden. Ideal für Grundsätze wie „Warenpost, wo es passt“ oder „über 1.000 € Warenwert zur Prüfung“.</div>
      <ShippingRuleEditor rules={rules} onChange={setRules} methods={methods} packagings={(packs.data ?? []).map((p) => ({ value: p.id, label: p.name }))} staff lockVisibility="internal" />
      <div className="flex justify-end"><Button variant="primary" icon={<Save className="size-4" />} disabled={JSON.stringify(rules) === JSON.stringify(q.data?.rules)} loading={save.isPending} onClick={() => save.mutate()}>Speichern</Button></div>
    </>
  );
}
